import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User, UserRole } from '../types'
import { simpleDirectCloudApi } from '../services/directCloudApi.simple'
import { auth, ensureLogin, callApi } from '../services/tcbClient'

interface AuthState {
  user: User | null
  isLoading: boolean
  login: (username: string, password: string) => Promise<void>
  logout: () => void
  checkPermission: (permission: string) => boolean
  hasRole: (role: UserRole) => boolean
  checkRouteAccess: (route: string) => boolean
  getDefaultRoute: () => string
}

const normalizeRole = (role: any): UserRole => {
  const raw = String(role || '').toLowerCase()
  if (raw === 'a' || raw === 'b' || raw === 'c' || raw === 'd') return raw.toUpperCase() as UserRole
  if (raw === 'boss') return 'A'
  if (raw === 'cashier') return 'A'
  if (raw === 'parts') return 'D'
  if (raw === 'accountant') return 'B'
  if (raw === 'vehicle_manager') return 'B'
  return 'C'
}

const convertCloudUserToLocal = (cloudUser: any): User => {
  const normalizedRole = normalizeRole(cloudUser.role)
  const preferredName =
    cloudUser.nickname ||
    cloudUser.nickName ||
    cloudUser.displayName ||
    cloudUser.username ||
    cloudUser._id ||
    cloudUser.id ||
    'user'
  return {
    id: cloudUser._id || cloudUser.id || cloudUser.uid || cloudUser.nickName,
    nickName: preferredName,
    nickname: cloudUser.nickname,
    info: cloudUser.info,
    username: cloudUser.username,
    makerSealUrl: cloudUser.makerSealUrl,
    avatarUrl: cloudUser.avatarUrl || getDefaultAvatar(normalizedRole),
    role: normalizedRole,
    createTime: cloudUser.createTime,
    loginTime: new Date().toISOString(),
    isActive: cloudUser.isActive !== false && cloudUser.isActive !== 0
  }
}

const getDefaultAvatar = (role: string): string => {
  const avatars = {
    A: 'A',
    B: 'B',
    C: 'C',
    D: 'D'
  }
  const normalized = String(role || '').toUpperCase()
  return avatars[normalized as keyof typeof avatars] || 'USER'
}

const ROLE_PERMISSIONS = {
  A: [
    'view_vehicles',
    'manage_vehicles',
    'manage_reminders',
    'confirm_payment',
    'update_payment_amount',
    'view_pending_accounting',
    'upload_vouchers',
    'confirm_accounting',
    'view_accounting_dashboard',
    'view_payment_confirmations',
    'submit_payment_info',
    'verify_accounting',
    'view_cashier_dashboard',
    'view_rent_records',
    'view_boss',
    'view_vehicle_adjustments',
    'manage_vehicle_adjustments'
  ],
  B: [
    'view_vehicles',
    'manage_vehicles',
    'manage_reminders',
    'confirm_payment',
    'update_payment_amount',
    'view_pending_accounting',
    'upload_vouchers',
    'confirm_accounting',
    'view_accounting_dashboard'
  ],
  C: [
    'view_vehicles'
  ],
  D: [
    'view_vehicle_adjustments',
    'manage_vehicle_adjustments'
  ]
} as const

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isLoading: false,

      login: async (username: string, password: string) => {
        set({ isLoading: true })

        try {
          if (!username || !password) {
            throw new Error('Username or password is empty')
          }

          let baseProfile: any = null

          try {
            console.log('[auth] signIn with CloudBase Auth:', username)
            await auth.signIn({ username, password })
          } catch (cloudAuthError) {
            console.warn('[auth] cloud auth failed, fallback to api password login:', cloudAuthError)
            const fallback = await callApi('signInWithPassword', { username, password })
            if (!fallback?.success || !fallback?.data) {
              const fallbackMessage = fallback?.error || fallback?.message
              if (fallbackMessage) {
                throw new Error(fallbackMessage)
              }
              throw cloudAuthError
            }
            baseProfile = fallback.data
          }

          await ensureLogin()

          console.log('[auth] lookup user profile:', username)
          const result = await simpleDirectCloudApi.getUserByIdentity(username)

          if (!result.success || !result.data) {
            if (baseProfile) {
              const user = convertCloudUserToLocal({
                ...baseProfile,
                username,
                nickName: baseProfile?.nickName || username
              })
              console.log('[auth] login success with fallback profile', user)
              set({ user, isLoading: false })
              return
            }
            throw new Error('用户不存在，请联系管理员添加账号')
          }

          const mergedProfile = {
            ...baseProfile,
            ...result.data,
            username: result.data?.username || baseProfile?.username || username,
            nickName: result.data?.nickName || baseProfile?.nickName || username,
            makerSealUrl: result.data?.makerSealUrl || baseProfile?.makerSealUrl
          }
          const user = convertCloudUserToLocal(mergedProfile)
          console.log('[auth] login success', user)

          set({ user, isLoading: false })
        } catch (error) {
          console.error('[auth] login failed', error)
          set({ isLoading: false })
          throw error
        }
      },

      logout: () => {
        auth.signOut().catch(() => {})
        set({ user: null })
      },

      checkPermission: (permission: string) => {
        const { user } = get()
        if (!user) return false

        const rolePermissions = ROLE_PERMISSIONS[user.role]
        return rolePermissions.includes(permission as any)
      },

      hasRole: (role: UserRole) => {
        const { user } = get()
        return user?.role === role
      },

      checkRouteAccess: (route: string) => {
        const { user } = get()
        if (!user) return false

        if (route.startsWith('/vehicles/')) {
          return ['A', 'B', 'C'].includes(user.role)
        }

        const routePermissions: Record<string, UserRole[]> = {
          '/': ['A', 'B', 'C', 'D'],
          '/vehicles': ['A', 'B', 'C'],
          '/vehicle-manager': ['A', 'B', 'C'],
          '/accountant': ['A', 'B'],
          '/cashier': ['A'],
          '/parts': ['A', 'D'],
          '/boss': ['A']
        }

        const allowedRoles = routePermissions[route]
        return allowedRoles ? allowedRoles.includes(user.role) : false
      },

      getDefaultRoute: () => {
        const { user } = get()
        if (!user) return '/login'

        const defaultRoutes: Record<UserRole, string> = {
          A: '/boss',
          B: '/vehicle-manager',
          C: '/vehicle-manager',
          D: '/parts'
        }

        return defaultRoutes[user.role] || '/vehicles'
      }
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ user: state.user })
    }
  )
)
